import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { listNotifications, markNotificationsRead } from './api'
import { queryKeys } from '../../lib/query-keys'
import { usePrivateBroadcast } from '../../lib/realtime'
import { formatDateTime } from '../../lib/utils'

export function NotificationPanel({ userId }: { userId: string }) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const client = useQueryClient()
  const notifications = useQuery({ queryKey: queryKeys.notifications, queryFn: listNotifications })
  const refresh = useCallback(() => {
    void client.invalidateQueries({ queryKey: queryKeys.notifications })
  }, [client])
  usePrivateBroadcast(`user:${userId}`, refresh)
  const mark = useMutation({ mutationFn: () => markNotificationsRead(), onSuccess: refresh })
  const unread = notifications.data?.filter((n) => !n.readAt).length ?? 0
  const close = () => {
    setOpen(false)
    requestAnimationFrame(() => trigger.current?.focus())
  }
  return (
    <div className="notification-control">
      <button
        ref={trigger}
        className="icon-button notification-button"
        aria-label={`Notifikasi${unread ? `, ${unread} belum dibaca` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Bell />
        {unread ? <span>{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <aside className="notification-panel" aria-label="Notifikasi">
          <header>
            <h2>Notifikasi</h2>
            <button className="icon-button" aria-label="Tutup notifikasi" onClick={close}>
              <X />
            </button>
          </header>
          <div>
            {notifications.data?.length ? (
              notifications.data.map((item) => (
                <article className={item.readAt ? '' : 'is-unread'} key={item.id}>
                  {item.actionUrl ? (
                    <Link to={item.actionUrl} onClick={close}>
                      <strong>{item.title}</strong>
                      <p>{item.body}</p>
                    </Link>
                  ) : (
                    <>
                      <strong>{item.title}</strong>
                      <p>{item.body}</p>
                    </>
                  )}
                  <time>{formatDateTime(item.createdAt)}</time>
                </article>
              ))
            ) : (
              <p className="notification-empty">Tiada notifikasi.</p>
            )}
          </div>
          {unread ? (
            <footer>
              <Button variant="ghost" busy={mark.isPending} onClick={() => mark.mutate()}>
                <CheckCheck /> Tandakan semua dibaca
              </Button>
            </footer>
          ) : null}
        </aside>
      ) : null}
    </div>
  )
}
